import React, { useMemo, useCallback } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { KarateGroupedSidebar } from "@/components/navigation/KarateGroupedSidebar.jsx";
import {
  HelpButton as CoquilleHelpButton,
  HelpDrawer,
  HelpProvider,
  useHelp,
  GsdConnectedShell,
  GsdAppShell,
  LanguageToggle,
  useI18n,
  injectCoquilleTokens,
  GsdReadableBlock,
  getLocaleLabel,
} from "@/coquille-bridge";
import { resolveKarateShellMode, toGsdShellMode } from "@/config/karateRoleShell.js";
import {
  getAdminNavGroups,
  getAdminMobileNavItems,
  findAdminNavGroupForPath,
  getManagerNavGroups,
  getManagerMobileNavItems,
  findManagerNavGroupForPath,
  getAssistantNavGroups,
  getAssistantMobileNavItems,
  findAssistantNavGroupForPath,
  getPlayerTabItems,
  getParentNavItems,
} from "@/config/karateNavConfig.js";
import { getHelpTopicRegistry, getHelpFallbackTopic } from "@/content/helpContent.js";
import { resolveHelpScreenId } from "@/config/resolveHelpScreen.js";
import { useAuth } from "@/context/AuthContext.jsx";
import { KarateBackgroundShell } from "@/components/KarateBackgroundShell.jsx";
import { useRandomKarateBackground } from "@/hooks/useRandomKarateBackground.js";
import { karateAppConfig } from "@/config/karateAppConfig.js";
import { APP_VERSION } from "@/config/appVersion.js";
import { karateI18nNamespaces, tApp } from "@/i18n/karateI18n.js";
import { useKarateUi } from "@/hooks/useKarateUi.js";
import { getDemoUserDisplayName } from "@/data/dojoOsOrgModel.js";
import { localizeDemoText } from "@/i18n/localizeDemoText.js";
import { KarateHeaderLogo } from "@/components/KarateHeaderLogo.jsx";
import { DataSourceBanner } from "@/components/DataSourceBanner.jsx";
import { KarateHeaderProfileMenu } from "@/components/avatar/KarateHeaderProfileMenu.jsx";
import { getStoredBackendUser } from "@/api/karateBackendApi.js";
import { DEMO_USER_REGISTRY } from "@/data/dojoOsOrgModel.js";

injectCoquilleTokens();

function resolveHelpKey(pathname, search = "") {
  if (pathname === "/admin/lancement-1er-septembre" || pathname.startsWith("/admin/lancement-1er-septembre")) {
    const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`);
    const etape = params.get("etape");
    if (etape && /^step-[1-7]$/.test(etape)) {
      return `launch-cockpit-${etape}`;
    }
    return "launch-cockpit";
  }
  return resolveHelpScreenId(pathname);
}

function HelpDrawerConnected() {
  const { open, topic, closeHelp } = useHelp();
  const { helpDrawerLabels } = useKarateUi();
  return (
    <HelpDrawer
      open={open}
      onClose={closeHelp}
      topic={topic}
      testId="help-drawer"
      closeLabel={helpDrawerLabels.close}
      labels={helpDrawerLabels}
    />
  );
}

function getGroupedSidebarConfig(karateShellMode, locale, t) {
  const adminTitle = tApp(t, "administration", locale === "en" ? "Administration" : "Administration");
  switch (karateShellMode) {
    case "admin":
      return {
        groups: getAdminNavGroups(locale),
        findGroupForPath: findAdminNavGroupForPath,
        mobileNav: getAdminMobileNavItems(locale),
        testId: "karate-admin-grouped-sidebar",
        title: adminTitle,
        subtitle:
          locale === "en"
            ? tApp(t, "platformManagement", "Platform management")
            : tApp(t, "platformManagement", "Gestion plateforme"),
        ariaLabel: adminTitle,
      };
    case "manager":
      return {
        groups: getManagerNavGroups(locale),
        findGroupForPath: findManagerNavGroupForPath,
        mobileNav: getManagerMobileNavItems(locale),
        testId: "karate-manager-grouped-sidebar",
        title: locale === "en" ? "Sensei space" : "Espace Sensei",
        subtitle: locale === "en" ? "My dojo" : "Mon dojo",
        ariaLabel: locale === "en" ? "Sensei navigation" : "Navigation Sensei",
      };
    case "assistant":
      return {
        groups: getAssistantNavGroups(locale),
        findGroupForPath: findAssistantNavGroupForPath,
        mobileNav: getAssistantMobileNavItems(locale),
        testId: "karate-assistant-grouped-sidebar",
        title: locale === "en" ? "Sempai space" : "Espace Sempai",
        subtitle: locale === "en" ? "Assisted classes" : "Classes assistées",
        ariaLabel: locale === "en" ? "Sempai navigation" : "Navigation Sempai",
      };
    default:
      return null;
  }
}

function HelpButtonConnected() {
  const { openHelp } = useHelp();
  return <CoquilleHelpButton onClick={openHelp} testId="help-button" />;
}

function KarateBrand({ locale }) {
  return (
    <Link
      to="/"
      className="karate-header-brand gsd-top-header__brand"
      data-testid="brand-link"
      aria-label={locale === "en" ? "Shotokan AAMC — Home" : "Shotokan AAMC — Accueil"}
    >
      <KarateHeaderLogo />
      <span className="karate-header-brand__title" data-testid="brand-title">
        Shotokan AAMC
      </span>
    </Link>
  );
}

export function AppLayout() {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    name,
    username,
    role,
    activeRoleUi,
    logout,
    canSwitchRole,
    switchActiveRole,
    demoRoles,
    canChangePassword,
  } = useAuth();
  const location = useLocation();
  const handleLogout = useCallback(async () => {
    await logout();
    navigate("/connexion", { replace: true });
  }, [logout, navigate]);
  const { lang, t } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  const userDisplayName = useMemo(() => {
    if (!isAuthenticated) return undefined;
    if (username) {
      const fromRegistry = getDemoUserDisplayName(username, locale);
      if (fromRegistry && fromRegistry !== username) return fromRegistry;
    }
    return localizeDemoText(name, locale);
  }, [isAuthenticated, username, name, locale]);

  const personKey = useMemo(() => {
    const backendUser = getStoredBackendUser();
    if (backendUser?.personId) return backendUser.personId;
    if (username && DEMO_USER_REGISTRY[username]?.instructorId) return DEMO_USER_REGISTRY[username].instructorId;
    return username || null;
  }, [username, isAuthenticated]);
  const helpScreenId = resolveHelpKey(location.pathname, location.search);
  const helpTopicRegistry = useMemo(() => getHelpTopicRegistry(locale), [locale]);
  const helpFallback = useMemo(() => getHelpFallbackTopic(locale), [locale]);
  const { background, isAdmin: isAdminRoute, fading } = useRandomKarateBackground();

  const shellRole = isAuthenticated ? activeRoleUi : "guest";
  const karateShellMode = resolveKarateShellMode(shellRole, { isAuthenticated, pathname: location.pathname });
  const shellMode = toGsdShellMode(karateShellMode);
  const groupedConfig = getGroupedSidebarConfig(karateShellMode, locale, t);

  const playerTabItems =
    karateShellMode === "player"
      ? getPlayerTabItems(locale)
      : karateShellMode === "parent"
        ? getParentNavItems(locale)
        : [];

  const publicNavGroup =
    shellMode === "public"
      ? [
          {
            id: "public",
            label: locale === "en" ? "Public" : "Public",
            items:
              karateAppConfig.navGroups
                .find((g) => g.id === "public")
                ?.items.map((item) => ({ ...item, label: getLocaleLabel(item, locale) })) || [],
          },
        ]
      : [];

  const renderNavLink = (item, { className, children, onNavigate }) => (
    <NavLink
      to={item.to || "/"}
      end={item.end}
      className={({ isActive }) => (isActive ? `${className} gsd-nav-item--active` : className)}
      onClick={() => {
        onNavigate?.();
        window.dispatchEvent(new Event("karate-nav-close"));
      }}
      data-testid={`nav-${item.id}`}
    >
      {children}
    </NavLink>
  );

  const isItemActive = (item) => {
    if (!item.to) return false;
    if (item.matchPrefixes?.length) {
      return item.matchPrefixes.some(
        (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
      );
    }
    if (item.end) return location.pathname === item.to;
    return location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
  };

  const groupedSidebarSlot = groupedConfig ? (
    <KarateGroupedSidebar
      groups={groupedConfig.groups}
      findGroupForPath={groupedConfig.findGroupForPath}
      renderLink={renderNavLink}
      isItemActive={isItemActive}
      pathname={location.pathname}
      title={groupedConfig.title}
      subtitle={groupedConfig.subtitle}
      ariaLabel={groupedConfig.ariaLabel}
      testId={groupedConfig.testId}
      groupTestIdPrefix="karate-nav-group"
    />
  ) : null;

  const mobileNavItems = groupedConfig?.mobileNav ?? (playerTabItems.length ? playerTabItems : []);

  const shellCommonProps = {
    config: karateAppConfig,
    locale,
    activeRole: shellRole,
    isAuthenticated,
    pathname: location.pathname,
    shellMode,
    userDisplayName,
    version: APP_VERSION,
    brand: <KarateBrand locale={locale} />,
    languageToggle: <LanguageToggle testId="karate-language-toggle" />,
    helpSlot: <HelpButtonConnected />,
    userContextSlot:
      isAuthenticated && userDisplayName ? (
        <KarateHeaderProfileMenu
          displayName={userDisplayName}
          email={username}
          personKey={personKey}
          locale={locale}
          activeRoleUi={shellRole}
          switchableRoles={demoRoles}
          canSwitchRole={canSwitchRole}
          canChangePassword={canChangePassword}
          onRoleChange={switchActiveRole}
          onLogout={handleLogout}
        />
      ) : null,
    userContextBar:
      isAuthenticated && userDisplayName ? (
        <KarateHeaderProfileMenu
          displayName={userDisplayName}
          email={username}
          personKey={personKey}
          locale={locale}
          activeRoleUi={shellRole}
          switchableRoles={demoRoles}
          canSwitchRole={canSwitchRole}
          canChangePassword={canChangePassword}
          onRoleChange={switchActiveRole}
          onLogout={handleLogout}
        />
      ) : null,
    loginSlot: (
      <Link to="/connexion" className="btn-primary btn-sm btn-login" data-testid="login-link">
        {tApp(t, "login", locale === "en" ? "Log in" : "Connexion")}
      </Link>
    ),
    onLogout: undefined,
    renderNavLink,
    isItemActive,
    roleSwitcherProps: null,
    navCloseEvent: "karate-nav-close",
    footerMetaLine: tApp(
      t,
      "footerMeta",
      locale === "en" ? karateI18nNamespaces.en.app.footerMeta : karateI18nNamespaces.fr.app.footerMeta,
    ),
    testId: "app-shell",
    shellWrapper: <KarateBackgroundShell background={background} isAdmin={isAdminRoute} fading={fading} />,
    mobileNavItems,
  };

  return (
    <HelpProvider
      pageContext={{ screenId: helpScreenId, role }}
      topicRegistry={helpTopicRegistry}
      fallbackTopic={helpFallback}
    >
      {groupedConfig ? (
        <GsdAppShell
          {...shellCommonProps}
          layoutVariant="sidebar"
          hideHeaderNav
          sidebarSlot={groupedSidebarSlot}
          navGroups={[]}
        >
          <div className="site-main site-main--wide">
            <DataSourceBanner />
            <Outlet />
          </div>
          <HelpDrawerConnected />
        </GsdAppShell>
      ) : (
        <GsdConnectedShell
          {...shellCommonProps}
          sidebarItems={[]}
          playerTabItems={playerTabItems}
          navGroups={publicNavGroup}
        >
          <div className="site-main site-main--wide">
            <DataSourceBanner />
            <Outlet />
          </div>
          <HelpDrawerConnected />
        </GsdConnectedShell>
      )}
    </HelpProvider>
  );
}

export function PageSection({ title, subtitle, children, testId = "page-section" }) {
  return (
    <GsdReadableBlock testId={testId} className="page-section page-section--polish" variant="default">
      <header className="page-header page-header--polish">
        <h1>{title}</h1>
        {subtitle ? <p className="page-subtitle">{subtitle}</p> : null}
      </header>
      {children}
    </GsdReadableBlock>
  );
}
